import React, { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { CellContext, type ColumnDef } from '@tanstack/react-table';
import useOffers from 'api/useOffers';
import usePricing from 'api/usePricing';
import type {
  Offer,
  ProductsSequence,
  TimeFrameObject
} from 'common/contracts';
import { DATE_TIME_FORMAT_SHORTER } from 'constants/constants';
import { EAppearanceStatus, EOfferStatus, OfferType } from 'constants/enums';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { usePageTypeByFeatureFlag } from 'hooks/usePageTypeByFF';
import { getConvertedPrice } from 'utils/getConvertedPrice';
import { appearanceUtils, statusUtils } from 'utils/scheduleUtils';
import { statusFilterOptions } from 'utils/selectOptionsUtils';
import { EToasterStatus, showToast } from 'utils/showToast';

import Button from 'components/Button/Button';
import type { ChipStatus } from 'components/Chip/Chip';
import Chip from 'components/Chip/Chip';
import FilterDropdownCard from 'components/Filter/FilterDropdownCard';
import FilterSearchCard from 'components/Filter/FilterSearchCard';

import ActionCell from '../../components/DataTableComponent/components/ActionCell';
import DataTable from '../../components/DataTableComponent/DataTable';
import PortalHeader from '../../components/PortalHeader/PortalHeader';
const dayjsUTC = dayjs.extend(utc);

type RowData = {
  publisherOfferId: string;
  offerId: string;
  productsSequence: ProductsSequence[];
  createdAt: string;
  expirationDate: string;
  status: string;
  active: boolean;
  schedule?: {
    permanent: boolean;
    interval?: string;
    timeFrames?: TimeFrameObject[];
  };
  deeplinkUrl: string;
};

const statusMapping: Record<string, ChipStatus> = {
  [EOfferStatus.RUNNING]: 'success',
  [EOfferStatus.PERMANENT]: 'active',
  [EOfferStatus.UPCOMING]: 'warning',
  [EOfferStatus.ENDED]: 'secondary',
  [EOfferStatus.INTERVAL]: 'optional',
  [EOfferStatus.INACTIVE]: 'danger'
};

const CheckoutLinksTable = () => {
  const navigate = useNavigate();

  const { getOffers, deleteOffer, updateOffer } = useOffers({
    offerId: undefined,
    offerType: OfferType.CHECKOUT_LINK
  });
  const [selectedOffer, setSelectedOffer] = useState<string | null>(null);
  const [skuFilterValue, setSkuFilterValue] = useState<string>('');
  const [statusFilterValue, setStatusFilterValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const { getPricings } = usePricing();
  const { isPageDisabled } = usePageTypeByFeatureFlag('Checkout Links');

  const isEmpty = getOffers.data?.offers && getOffers.data.offers.length === 0;

  useEffect(() => {
    if (!selectedOffer) return;
    deleteOffer.mutate(selectedOffer, {
      onSuccess: () => {
        showToast({
          message: 'Checkout link deleted successfully',
          status: EToasterStatus.SUCCESS
        });
        getOffers.refetch();
      },
      onError: (e: any) => {
        showToast({
          message: 'Failed to delete checkout link',
          status: EToasterStatus.ERROR,
          description: e.response.data.message ?? ''
        });
      },
      onSettled: () => {
        setSelectedOffer(null);
      }
    });
  }, [selectedOffer]);

  useEffect(() => {
    setIsLoading(
      getOffers.isFetching ||
        getOffers.isRefetching ||
        getOffers.isLoading ||
        getPricings.isRefetching
    );
  }, [
    getOffers.data?.offers,
    getOffers.isRefetching,
    getPricings.isRefetching
  ]);

  useEffect(() => {
    getPricings.refetch();
  }, []);

  const filteredData = getOffers?.data?.offers.filter((offer: Offer) => {
    const isSkuMatch = offer.publisherOfferId
      .toLowerCase()
      .includes(skuFilterValue.toLowerCase());

    const offerStatus = statusUtils.getOfferStatus(offer, offer.active);

    return (
      (!statusFilterValue ||
        statusFilterValue === 'All' ||
        statusFilterValue === offerStatus ||
        (statusFilterValue === EOfferStatus.INACTIVE &&
          offerStatus === EOfferStatus.INACTIVE)) &&
      isSkuMatch
    );
  }) || [];

  const columns: ColumnDef<RowData, unknown>[] = [
    {
      accessorKey: 'deeplinkUrl',
      header: 'Links',
      cell: (cell: CellContext<RowData, unknown>) => {
        const { deeplinkUrl } = cell.row.original;

        return (
          <Button
            onClick={() => handleGetCheckoutLink(deeplinkUrl)}
            className="text-gray-600 hover:text-black w-15"
            variant="ghost"
          >
            <FontAwesomeIcon icon="fa-solid fa-link" />
          </Button>
        );
      }
    },
    {
      accessorKey: 'publisherOfferId',
      header: 'External ID',
      cell: (cell: CellContext<RowData, unknown>) => cell.getValue()
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: (cell: CellContext<RowData, unknown>) => cell.getValue()
    },
    {
      accessorKey: 'price',
      header: 'Price',
      cell: (cell: CellContext<RowData, unknown>) => {
        if (getPricings.isLoading) return '';
        const productsSequence = cell.row.original
          .productsSequence as ProductsSequence[];
        return getConvertedPrice(
          {
            row: { productsSequence: productsSequence || [] }
          },
          getPricings.data.result
        );
      }
    },
    {
      accessorKey: 'createdAt',
      header: 'Created at',
      cell: (cell: CellContext<RowData, unknown>) =>
        dayjsUTC
          .utc(cell.getValue() as boolean)
          .format(DATE_TIME_FORMAT_SHORTER)
    },
    {
      accessorKey: 'expirationDate',
      header: 'Expiration date',
      cell: (cell: CellContext<RowData, unknown>) => {
        const createdAt = cell.row.original.createdAt;
        if (!createdAt) return <div className="text-black">-</div>;

        const expirationDate = new Date(createdAt);
        expirationDate.setMonth(expirationDate.getMonth() + 6);

        return dayjs(expirationDate).format(DATE_TIME_FORMAT_SHORTER);
      }
    },
    {
      accessorKey: 'active',
      header: 'Status',
      cell: (cell: CellContext<RowData, unknown>) => {
        const isActive = cell.getValue() as boolean;
        const status = statusUtils.getOfferStatus(cell.row.original, isActive);

        return (
          <Chip variant="flat" status={statusMapping[status]}>
            {status}
          </Chip>
        );
      }
    },
    {
      accessorKey: 'nextAppearance',
      header: 'Next appearance',
      cell: (cell: CellContext<RowData, unknown>) => {
        const schedule = cell.row.original.schedule;

        if (!schedule) {
          return <>-</>;
        }

        if (schedule.permanent) {
          if (schedule.interval) {
            return (
              <>
                {appearanceUtils.getNextAppearanceByRefreshTime(
                  schedule.interval
                )}
              </>
            );
          }
          return <>-</>;
        }

        const nextUpcomingTime = appearanceUtils.getNextUpcomingTime(
          schedule.timeFrames
        );

        return <>{nextUpcomingTime || '-'}</>;
      }
    },
    {
      accessorKey: 'actions',
      header: '',
      maxSize: 5,
      size: 5,
      cell: (cell: CellContext<RowData, unknown>) => {
        const { original } = cell.row;
        return (
          <ActionCell
            rowData={original}
            actions={[
              {
                label: original.active ? 'Deactivate' : 'Activate',
                handler: () =>
                  handleToggleActiveStatus(original.offerId, original.active)
              },
              {
                label: 'Edit',
                handler: () => navigate(`./form/${original.offerId}`)
              },
              {
                label: 'Duplicate',
                handler: () => navigate(`./dup/${original.offerId}`)
              },
              {
                label: 'Delete',
                handler: () => {
                  setSelectedOffer(original.offerId);
                }
              }
            ]}
          />
        );
      }
    }
  ];

  const handleGetCheckoutLink = (deeplinkUrl: string) => {
    const tempInput = document.createElement('input');
    tempInput.setAttribute('readonly', ''); // Prevent virtual keyboard on iOS
    tempInput.value = deeplinkUrl;
    tempInput.style.position = 'absolute';
    tempInput.style.left = '-9999px'; // Move off-screen
    document.body.appendChild(tempInput);

    try {
      if (navigator.userAgent.match(/ipad|iphone/i)) {
        // iOS specific handling
        tempInput.contentEditable = 'true';
        tempInput.readOnly = false;

        // Select the text
        const range = document.createRange();
        range.selectNodeContents(tempInput);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        tempInput.setSelectionRange(0, 999999);
      } else {
        // All other browsers
        tempInput.select();
      }

      document.execCommand('copy');
      showToast({
        message: 'Checkout link copied to clipboard',
        status: EToasterStatus.SUCCESS
      });
    } catch (err) {
      console.error('Copy failed:', err);
      showToast({
        message: 'Please copy manually: ' + deeplinkUrl,
        status: EToasterStatus.ERROR
      });
    } finally {
      document.body.removeChild(tempInput);
    }
  };

  const handleToggleActiveStatus = async (offerId: string, active: boolean) => {
    updateOffer.mutate(
      { offerId, form: { active: !active } },
      {
        onSuccess: () => {
          showToast({
            message: active
              ? 'Checkout link deactivated successfully'
              : 'Checkout link activated successfully',
            status: EToasterStatus.SUCCESS
          });
          getOffers.refetch();
        },
        onError: () => {
          showToast({
            message: 'Error editing checkout link',
            status: EToasterStatus.ERROR
          });
        }
      }
    );
  };

  return (
    <div
      className={`${isPageDisabled ? 'opacity-50 pointer-events-none' : 'opacity-100 pointer-events-auto'}`}
    >
      <PortalHeader
        title="Checkout Links"
        primaryButtonText="Create New Link"
        onPrimaryButtonClick={() => navigate('./form')}
        isCreateButton
      />
      {!isEmpty && (
        <div className="flex px-9 gap-3">
          <FilterSearchCard
            header="External ID"
            filterValue={skuFilterValue}
            setFilterValue={setSkuFilterValue}
            isLabelSearchBy
          />
          <FilterDropdownCard
            header="Status"
            filterValue={statusFilterValue}
            setFilterValue={setStatusFilterValue}
            options={statusFilterOptions}
          />
        </div>
      )}
      <div className="px-9 pb-9 pt-2.5">
        <DataTable
          data={filteredData}
          columns={columns}
          rowIdIdentifier="offerId"
          isLoading={isLoading}
          emptyStateConfig={{
            title: 'Checkout link',
            isEmpty,
            description: (
              <>
                Checkout links simplify purchases: one tap, auto-login, <br />
                preselected offers and amounts.
              </>
            ),
            onClick: () => navigate('./form')
          }}
          bordered
        />
      </div>
    </div>
  );
};

export default CheckoutLinksTable;
